import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import Modal from "@/components/ui/UIElements/Modal";
import { Icon } from "@/components/ui";
import { Button } from "@/components/ui/UIElements/Button";
import { getWhatsAppTestPayload } from "@/lib/whatsappTestLink";

const WhatsAppTestModal = ({ isOpen, onClose, agent }) => {
  const { t } = useTranslation("aiAgents");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);

  const { waUrl, isReady, countryCodeDigits, phoneDigits } = useMemo(
    () => getWhatsAppTestPayload(agent),
    [agent],
  );

  useEffect(() => {
    let isCancelled = false;

    const generateQrCode = async () => {
      if (!isOpen || !waUrl) {
        setQrCodeDataUrl("");
        setIsGeneratingQr(false);
        return;
      }

      setIsGeneratingQr(true);
      try {
        const dataUrl = await QRCode.toDataURL(waUrl, {
          width: 256,
          margin: 1,
        });
        if (!isCancelled) {
          setQrCodeDataUrl(dataUrl);
        }
      } catch {
        if (!isCancelled) {
          setQrCodeDataUrl("");
        }
      } finally {
        if (!isCancelled) {
          setIsGeneratingQr(false);
        }
      }
    };

    generateQrCode();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, waUrl]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("whatsappTestModal.title")}
      subtitle={t("whatsappTestModal.description")}
      maxWidth="max-w-md"
    >
      {!isReady ? (
        <div className="ordering-ai-agent-modal-content space-y-4">
          <div className="border-warning rounded-lg border bg-[var(--warning-bg)] p-4">
            <div className="flex items-start gap-3">
              <Icon
                name="AlertTriangle"
                className="text-warning mt-0.5 h-5 w-5 flex-shrink-0"
              />
              <p className="text-warning text-sm">
                {t("card.actions.testWhatsAppMissingData")}
              </p>
            </div>
          </div>
          <Button
            type="button"
            emphasis="outline"
            intent="neutral"
            size="touch"
            onClick={onClose}
            className="w-full"
          >
            {t("actions.cancel")}
          </Button>
        </div>
      ) : (
        <div className="ordering-ai-agent-modal-content space-y-4">
          <div className="border-border bg-card/70 rounded-lg border p-4">
            <p className="text-muted-foreground text-sm">
              <span className="text-foreground font-medium">
                {t("whatsappTestModal.phoneLabel")}:
              </span>{" "}
              +{countryCodeDigits} {phoneDigits}
            </p>
          </div>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center rounded-lg bg-[var(--od-success-signal)] px-4 py-2.5 text-sm font-medium text-[var(--od-ink-950)] transition-colors hover:bg-[var(--od-success-signal)]"
          >
            <Icon name="ExternalLink" className="me-2 h-4 w-4" />
            {t("whatsappTestModal.openLink")}
          </a>

          <div className="border-border bg-card/50 rounded-lg border p-4">
            <p className="text-muted-foreground mb-3 text-sm">
              {t("whatsappTestModal.qrHint")}
            </p>
            <div className="flex items-center justify-center rounded-md bg-white p-3">
              {qrCodeDataUrl ? (
                <img
                  src={qrCodeDataUrl}
                  alt={t("whatsappTestModal.qrAlt")}
                  className="h-56 w-56"
                />
              ) : (
                <div className="flex h-56 w-56 items-center justify-center">
                  {isGeneratingQr ? (
                    <div className="border-border h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      {t("card.actions.testWhatsAppMissingData")}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default WhatsAppTestModal;
